import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Film, Camera, Shield, BookMarked, AudioLines, CheckCircle2, XCircle,
  AlertTriangle, Ban, Clapperboard, Cpu, ArrowRight, ScanEye, Lock, Wand2,
  PenLine, LayoutGrid, ReceiptText, History, Lightbulb, Rocket,
  Save, Copy, Archive, RotateCcw, GitCompare, Pencil, ListChecks, Search, FolderOpen,
  PlayCircle, Download, PowerOff, ShieldAlert, Eye,
} from "lucide-react";
import api from "@/lib/api";
import { CinematicStage, StageHeader, GlassCard, Pill, Spinner, cx } from "@/components/shared";

const RISK_TONE = { LOW: "green", MEDIUM: "gold", HIGH: "red" };
const STATUS_TONE = {
  APPROVED: "green", CANCELLED: "slate", EXPIRED: "gold", ESTIMATE_REQUIRED: "gold",
  INVALIDATED: "red", ESTIMATED: "gold", DRAFT: "slate", ARCHIVED: "slate",
};
const CATEGORIES = ["KIDS", "CONVERSATION", "ACTION", "SPORTS", "PRODUCT", "LUXURY",
  "TRAVEL", "EMOTIONAL", "HORROR", "COMEDY", "FANTASY", "DOCUMENTARY", "SOCIAL_SHORT"];
const FRAMINGS = ["wide", "medium_wide", "medium", "two_shot", "medium_close", "close_up"];
const MOVEMENTS = ["locked", "slow_dolly_in", "push_in", "pull_out", "lateral_track",
  "follow", "subtle_handheld", "orbit"];
const SPEEDS = ["normal", "fast_motion", "micro_slow_motion", "slow_motion", "speed_ramp", "freeze_hold"];
const CHECK_LABELS = {
  model_selected: "Model selected", resolution_selected: "Resolution selected",
  duration_selected: "Duration selected", audio_selected: "Audio setting selected",
  cost_verified: "Cost verified", cost_below_hard_cap: "Cost below hard cap",
  fingerprint_matches_estimate: "Fingerprint matches estimate", no_stale_estimate: "No stale estimate",
  no_material_changes_pending: "No material changes pending", not_archived: "Not archived",
  retry_disabled: "Retry disabled", fallback_disabled: "Fallback disabled",
  variants_disabled: "Variants disabled", generation_disabled: "Generation disabled",
};

function SectionTitle({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2.5 mb-4 mt-2">
      <div className="w-8 h-8 rounded-lg bg-[#5B4DFF]/15 grid place-items-center">
        <Icon className="w-4 h-4 text-[#5B4DFF]" />
      </div>
      <h3 className="font-display text-lg font-semibold text-white">{children}</h3>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 border-b border-white/5 last:border-0">
      <span className="text-xs font-mono uppercase tracking-wider text-slate-500">{k}</span>
      <span className="text-sm text-slate-200 text-right">{v}</span>
    </div>
  );
}

function SpeedFlow({ seq }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {seq.map((p, i) => (
        <span key={i} className="flex items-center gap-1.5">
          <span className="px-2 py-1 rounded-md bg-white/5 border border-white/10 text-xs text-slate-200">
            {p.speed.replace(/_/g, " ")}
          </span>
          {i < seq.length - 1 && <ArrowRight className="w-3 h-3 text-slate-600" />}
        </span>
      ))}
    </div>
  );
}

function ApprovalCard({ plan, estimate, hardCap, setHardCap, onEstimate, onApprove, onCancel, busy, approved }) {
  const c = plan.cost;
  const unknown = c.status === "UNKNOWN";
  const chosen = plan.model.chosen;
  const est = estimate || plan.approval_state?.estimate;
  const verified = est && est.status === "VERIFIED_TIMESTAMPED";
  const capNum = parseFloat(hardCap);
  const overCap = verified && !isNaN(capNum) && est.estimated_cost > capNum;
  const canApprove = verified && !overCap && !busy;

  return (
    <GlassCard className="p-6 border-[#FFB300]/30" data-testid="approval-card">
      <div className="flex items-center gap-2 mb-4">
        <Lock className="w-4 h-4 text-[#FFB300]" />
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#FFB300]">Paid Generation Approval</div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1 mb-4">
        <KV k="Model" v={chosen ? chosen.name : "NONE"} />
        <KV k="Duration" v={`${c.duration_s}s`} />
        <KV k="Resolution" v={c.resolution} />
        <KV k="Audio" v={c.audio ? "ON" : "OFF"} />
        <KV k="Estimated" v={verified ? `$${est.estimated_cost}` : unknown ? "UNKNOWN" : "—"} />
        <KV k="Maximum" v={verified ? `$${est.max_cost}` : "—"} />
        <KV k="Generations" v={c.generations} />
        <KV k="Retries" v={c.retries} />
        <KV k="Fallback" v={c.fallback ? "YES" : "NO"} />
        <KV k="Variants" v={c.variants} />
      </div>

      {/* hard spend cap */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-xs font-mono uppercase tracking-wider text-slate-500">Max spend cap $</span>
        <input data-testid="hard-cap-input" value={hardCap} onChange={(e) => setHardCap(e.target.value)}
          placeholder="none" inputMode="decimal"
          className="w-24 bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-sm text-white outline-none focus:border-[#5B4DFF]/50" />
        {overCap && <span className="text-xs text-red-300" data-testid="over-cap-note">exceeds cap</span>}
      </div>

      {unknown && (
        <div className="mb-4 flex items-start gap-2 text-sm text-amber-300 bg-amber-500/10 border border-amber-500/25 rounded-xl p-3" data-testid="unknown-cost-warning">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Credit-based model — a verified provider <span className="font-mono">/estimate</span> is required. Approval is blocked.</span>
        </div>
      )}

      {est && (
        <div className="mb-4 text-xs text-slate-400 bg-white/5 border border-white/10 rounded-xl p-3" data-testid="estimate-result">
          <div className="font-mono uppercase tracking-wider text-slate-500 mb-1">Estimate</div>
          <div>Status: <b className={verified ? "text-emerald-300" : "text-amber-300"}>{est.status}</b></div>
          <div>Source: {est.source}</div>
          {est.estimate_id && <div>ID: {est.estimate_id}</div>}
          {est.estimate_timestamp && <div>Timestamp: {new Date(est.estimate_timestamp).toLocaleString()}</div>}
        </div>
      )}

      <div className="flex items-center gap-2 text-xs text-slate-400 mb-4">
        <Ban className="w-3.5 h-3.5 text-red-400" />
        Executor is <b className="text-red-300 mx-1">DISABLED</b> — approval records a flag only.
      </div>

      {approved && (
        <div className="flex items-center gap-2 text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/25 rounded-xl p-3 mb-3" data-testid="approved-state-banner">
          <CheckCircle2 className="w-4 h-4" /> Approval recorded — generation NOT executed.
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <button data-testid="get-estimate-button" disabled={busy} onClick={onEstimate}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm bg-[#5B4DFF]/20 border border-[#5B4DFF]/40 text-[#c3b9ff] hover:bg-[#5B4DFF]/30 transition">
          <ReceiptText className="w-4 h-4" /> Get Verified Estimate
        </button>
        <button data-testid="approve-generation-button" disabled={!canApprove} onClick={onApprove}
          className={cx("inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition",
            canApprove ? "bg-[#FFB300] text-[#081229] hover:brightness-110 glow-gold"
              : "bg-white/5 border border-white/10 text-slate-600 cursor-not-allowed")}>
          <Clapperboard className="w-4 h-4" /> Approve
        </button>
        <button data-testid="cancel-approval-button" disabled={busy} onClick={onCancel}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm bg-white/5 border border-white/10 text-slate-300 hover:bg-white/10 transition">
          <XCircle className="w-4 h-4" /> Cancel
        </button>
      </div>
      {!verified && !unknown && (
        <p className="text-xs text-slate-500 mt-2" data-testid="estimate-required-hint">Get a verified estimate to unlock approval.</p>
      )}
      <div className="mt-4 flex items-center gap-2 text-xs text-slate-400" data-testid="future-generation-note">
        <Rocket className="w-3.5 h-3.5 text-[#5B4DFF]" /> {plan.future_generation_note}
      </div>
    </GlassCard>
  );
}

function ExplanationsCard({ ex }) {
  const rows = [
    ["Why this model?", ex.why_model], ["Why this camera?", ex.why_camera],
    ["Why this motion?", ex.why_motion], ["Why this VFX?", ex.why_vfx],
    ["How much could this cost?", ex.how_much],
  ];
  return (
    <GlassCard className="p-6" data-testid="explanations-card">
      <SectionTitle icon={Lightbulb}>Why these decisions?</SectionTitle>
      <div className="space-y-3">
        {rows.map(([q, a]) => (
          <div key={q}>
            <div className="text-sm font-semibold text-[#c3b9ff]">{q}</div>
            <p className="text-sm text-slate-300">{a}</p>
          </div>
        ))}
        <div>
          <div className="text-sm font-semibold text-amber-300">What could fail?</div>
          <ul className="mt-1 space-y-1">
            {ex.what_could_fail.map((f, i) => (
              <li key={i} className="text-sm text-slate-300 flex gap-2"><AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />{f}</li>
            ))}
          </ul>
        </div>
      </div>
    </GlassCard>
  );
}

function SafetyChecklist({ checklist }) {
  const order = ["model_selected", "resolution_selected", "duration_selected", "audio_selected",
    "cost_verified", "cost_below_hard_cap", "fingerprint_matches_estimate", "no_stale_estimate",
    "no_material_changes_pending", "not_archived", "retry_disabled", "fallback_disabled",
    "variants_disabled", "generation_disabled"];
  return (
    <GlassCard className="p-6" data-testid="safety-checklist">
      <SectionTitle icon={ListChecks}>Final Pre-Generation Check</SectionTitle>
      <div className="space-y-1.5">
        {order.map((k) => (
          <div key={k} className="flex items-center gap-2 text-sm" data-testid={`check-${k}`}>
            {checklist[k] ? <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              : <XCircle className="w-4 h-4 text-red-400" />}
            <span className="text-slate-300">{CHECK_LABELS[k]}</span>
          </div>
        ))}
      </div>
      <div className={cx("mt-4 rounded-xl p-3 text-sm border flex items-center gap-2",
        checklist.all_ready ? "bg-emerald-500/10 border-emerald-500/25 text-emerald-300"
          : "bg-amber-500/10 border-amber-500/25 text-amber-300")} data-testid="safety-summary">
        {checklist.all_ready ? <Lock className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
        {checklist.all_ready ? "PLAN LOCKED · COST VERIFIED · FINGERPRINT MATCH · WITHIN CAP" : "Review gate not yet satisfied"}
        <span className="ml-auto text-xs text-red-300">GENERATION: DISABLED</span>
      </div>
    </GlassCard>
  );
}

function Select({ value, options, onChange, testid }) {
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
      className="bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-xs text-white outline-none focus:border-[#5B4DFF]/50">
      {options.map((o) => <option key={o} value={o} className="bg-[#0b1024]">{o.replace(/_/g, " ")}</option>)}
    </select>
  );
}

function EditPanel({ plan, draft, setDraft, onApply, busy }) {
  const setOv = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const chars = plan.continuity_bible.characters.filter((c) => c && !c.startsWith("("));
  const dialogueBeats = plan.story.beats.filter((b) => b.dialogue);
  const dirty = Object.keys(draft).length > 0;
  return (
    <GlassCard className="p-6 border-[#5B4DFF]/25" data-testid="edit-panel">
      <SectionTitle icon={Pencil}>Edit Director Review</SectionTitle>
      <p className="text-xs text-slate-500 mb-4">Any material change creates a new version and invalidates the estimate & approval.</p>

      {/* classification */}
      <div className="mb-4">
        <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-1">Classification
          <span className="ml-2 text-slate-600">(director: {plan.classification.director_suggested || plan.classification.label}, {plan.classification_source})</span>
        </div>
        <Select testid="edit-classification" value={(draft.classification?.label) || plan.classification.label}
          options={CATEGORIES} onChange={(v) => setOv({ classification: { label: v } })} />
      </div>

      {/* speakers */}
      {chars.length > 0 && (
        <div className="mb-4">
          <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-1">Speakers</div>
          {chars.map((c) => (
            <div key={c} className="flex items-center gap-2 mb-1">
              <span className="text-xs text-slate-500 w-16">{c}</span>
              <input data-testid={`edit-speaker-${c}`} defaultValue={c}
                onChange={(e) => setOv({ speakers: { ...(draft.speakers || {}), [c]: e.target.value } })}
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-xs text-white outline-none focus:border-[#5B4DFF]/50" />
            </div>
          ))}
        </div>
      )}

      {/* dialogue */}
      {dialogueBeats.length > 0 && (
        <div className="mb-4">
          <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-1">Dialogue (verbatim)</div>
          {dialogueBeats.map((b) => (
            <div key={b.id} className="mb-2">
              <div className="text-xs text-slate-500">{b.dialogue.speaker} · {b.id}</div>
              <input data-testid={`edit-dialogue-${b.id}`} defaultValue={b.dialogue.line || ""}
                onChange={(e) => setOv({ dialogue: { ...(draft.dialogue || {}), [b.id]: e.target.value } })}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-sm text-white outline-none focus:border-[#5B4DFF]/50" />
            </div>
          ))}
        </div>
      )}

      {/* per-shot camera + motion + vfx */}
      <div className="mb-4">
        <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-1">Camera / Motion / VFX per shot</div>
        <div className="space-y-2">
          {plan.shots.map((s) => (
            <div key={s.shot_id} className="rounded-lg bg-white/5 border border-white/10 p-2" data-testid={`edit-shot-${s.shot_id}`}>
              <div className="text-xs text-[#FFB300] uppercase mb-1">{s.beat_type} · {s.shot_id}</div>
              <div className="flex flex-wrap items-center gap-2">
                <Select testid={`edit-framing-${s.shot_id}`} value={draft.camera?.[s.shot_id]?.framing || s.camera.framing}
                  options={FRAMINGS} onChange={(v) => setOv({ camera: { ...(draft.camera || {}), [s.shot_id]: { ...(draft.camera?.[s.shot_id] || {}), framing: v } } })} />
                <Select testid={`edit-movement-${s.shot_id}`} value={draft.camera?.[s.shot_id]?.movement || s.camera.movement}
                  options={MOVEMENTS} onChange={(v) => setOv({ camera: { ...(draft.camera || {}), [s.shot_id]: { ...(draft.camera?.[s.shot_id] || {}), movement: v } } })} />
                <Select testid={`edit-motion-${s.shot_id}`} value={draft.motion?.[s.shot_id] || s.motion_sequence[0]?.speed || "normal"}
                  options={SPEEDS} onChange={(v) => setOv({ motion: { ...(draft.motion || {}), [s.shot_id]: v } })} />
              </div>
              {s.vfx_selected.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {s.vfx_selected.map((v) => {
                    const removed = (draft.vfx_removed?.[s.shot_id] || []).includes(v.effect);
                    return (
                      <button key={v.effect} data-testid={`edit-remove-vfx-${s.shot_id}-${v.effect}`}
                        onClick={() => setOv({ vfx_removed: { ...(draft.vfx_removed || {}), [s.shot_id]: removed
                          ? (draft.vfx_removed[s.shot_id] || []).filter((x) => x !== v.effect)
                          : [...(draft.vfx_removed?.[s.shot_id] || []), v.effect] } })}
                        className={cx("px-2 py-0.5 rounded-md text-xs border transition",
                          removed ? "bg-red-500/20 border-red-500/40 text-red-300 line-through" : "bg-[#5B4DFF]/10 border-[#5B4DFF]/25 text-[#b6aaff]")}>
                        {v.effect.replace(/_/g, " ")} {removed ? "✕" : ""}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <button data-testid="apply-edits-button" disabled={!dirty || busy} onClick={onApply}
        className={cx("inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition",
          dirty ? "bg-[#5B4DFF] text-white hover:brightness-110" : "bg-white/5 border border-white/10 text-slate-600 cursor-not-allowed")}>
        <Pencil className="w-4 h-4" /> Apply Edits (new version)
      </button>
    </GlassCard>
  );
}


function LibraryView({ library, search, setSearch, statusFilter, setStatusFilter, sort, setSort,
  onOpen, onDuplicate, onArchive, onRestore, cmpA, cmpB, setCmpA, setCmpB, onCompare, cmpResult }) {
  const statuses = ["ALL", "DRAFT", "ESTIMATE_REQUIRED", "ESTIMATED", "APPROVED", "CANCELLED", "ARCHIVED"];
  let rows = library.filter((p) => (p.title || "").toLowerCase().includes(search.toLowerCase()));
  if (statusFilter !== "ALL") rows = rows.filter((p) => p.status === statusFilter);
  rows = [...rows].sort((a, b) => {
    if (sort === "cost") return (b.estimated_cost || 0) - (a.estimated_cost || 0);
    if (sort === "oldest") return new Date(a.created_at) - new Date(b.created_at);
    return new Date(b.updated_at) - new Date(a.updated_at);
  });
  return (
    <div className="space-y-6" data-testid="library-view">
      <GlassCard className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-slate-500" />
            <input data-testid="library-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search scene name…"
              className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white outline-none focus:border-[#5B4DFF]/50" />
          </div>
          <select data-testid="library-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white outline-none">
            {statuses.map((s) => <option key={s} value={s} className="bg-[#0b1024]">{s}</option>)}
          </select>
          <select data-testid="library-sort" value={sort} onChange={(e) => setSort(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white outline-none">
            <option value="updated" className="bg-[#0b1024]">Newest modified</option>
            <option value="oldest" className="bg-[#0b1024]">Oldest</option>
            <option value="cost" className="bg-[#0b1024]">Cost</option>
          </select>
        </div>
      </GlassCard>

      <GlassCard className="p-6" data-testid="library-table">
        <SectionTitle icon={FolderOpen}>ScenePlan Library</SectionTitle>
        {rows.length === 0 ? <p className="text-sm text-slate-500">No plans match.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-mono uppercase tracking-wider text-slate-500 border-b border-white/10">
                  <th className="py-2 pr-4">Title</th><th className="py-2 pr-4">Ver</th><th className="py-2 pr-4">Model</th>
                  <th className="py-2 pr-4">Cost</th><th className="py-2 pr-4">Status</th><th className="py-2 pr-4">Modified</th><th className="py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.plan_id} className="border-b border-white/5" data-testid={`library-row-${p.plan_id}`}>
                    <td className="py-2 pr-4 text-slate-200">{(p.title || "").slice(0, 30)}</td>
                    <td className="py-2 pr-4 text-slate-400">v{p.version}</td>
                    <td className="py-2 pr-4 text-slate-400">{p.model || "—"}</td>
                    <td className="py-2 pr-4 text-slate-300">{p.estimated_cost != null ? `$${p.estimated_cost}` : "—"}</td>
                    <td className="py-2 pr-4"><Pill tone={STATUS_TONE[p.status] || "slate"}>{p.status}</Pill></td>
                    <td className="py-2 pr-4 text-slate-500 text-xs">{new Date(p.updated_at).toLocaleDateString()}</td>
                    <td className="py-2">
                      <div className="flex gap-1.5">
                        <button data-testid={`lib-open-${p.plan_id}`} onClick={() => onOpen(p.plan_id)} title="Open"
                          className="p-1.5 rounded-md bg-white/5 border border-white/10 hover:bg-white/10"><FolderOpen className="w-3.5 h-3.5 text-slate-300" /></button>
                        <button data-testid={`lib-dup-${p.plan_id}`} onClick={() => onDuplicate(p.plan_id)} title="Duplicate"
                          className="p-1.5 rounded-md bg-white/5 border border-white/10 hover:bg-white/10"><Copy className="w-3.5 h-3.5 text-slate-300" /></button>
                        {p.archived ? (
                          <button data-testid={`lib-restore-${p.plan_id}`} onClick={() => onRestore(p.plan_id)} title="Restore"
                            className="p-1.5 rounded-md bg-white/5 border border-white/10 hover:bg-white/10"><RotateCcw className="w-3.5 h-3.5 text-slate-300" /></button>
                        ) : (
                          <button data-testid={`lib-archive-${p.plan_id}`} onClick={() => onArchive(p.plan_id)} title="Archive"
                            className="p-1.5 rounded-md bg-white/5 border border-white/10 hover:bg-white/10"><Archive className="w-3.5 h-3.5 text-slate-300" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>

      {/* Compare versions */}
      <GlassCard className="p-6" data-testid="compare-panel">
        <SectionTitle icon={GitCompare}>Compare Versions</SectionTitle>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <select data-testid="compare-a" value={cmpA} onChange={(e) => setCmpA(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white">
            <option value="" className="bg-[#0b1024]">Plan A…</option>
            {library.map((p) => <option key={p.plan_id} value={p.plan_id} className="bg-[#0b1024]">{p.title?.slice(0, 24)} v{p.version}</option>)}
          </select>
          <select data-testid="compare-b" value={cmpB} onChange={(e) => setCmpB(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white">
            <option value="" className="bg-[#0b1024]">Plan B…</option>
            {library.map((p) => <option key={p.plan_id} value={p.plan_id} className="bg-[#0b1024]">{p.title?.slice(0, 24)} v{p.version}</option>)}
          </select>
          <button data-testid="run-compare-button" onClick={onCompare}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm bg-[#5B4DFF]/20 border border-[#5B4DFF]/40 text-[#c3b9ff] hover:bg-[#5B4DFF]/30">
            <GitCompare className="w-4 h-4" /> Compare</button>
        </div>
        {cmpResult && (
          <div data-testid="compare-result" className="space-y-1.5">
            <div className="text-xs text-slate-500 mb-1">Changed: {cmpResult.changed_fields.join(", ") || "none"}</div>
            {cmpResult.diffs.filter((d) => d.changed).map((d) => (
              <div key={d.field} className="text-sm rounded-lg bg-amber-500/5 border border-amber-500/20 p-2">
                <span className="font-mono text-xs uppercase text-amber-300">{d.field}</span>
                <div className="text-xs text-slate-400 mt-1">A: {JSON.stringify(d.a).slice(0, 80)}</div>
                <div className="text-xs text-slate-400">B: {JSON.stringify(d.b).slice(0, 80)}</div>
              </div>
            ))}
          </div>
        )}
      </GlassCard>
    </div>
  );
}

function ExecutionPanel({ config, review, understand, setUnderstand, onAuthorize, onDryRun, result, busy }) {
  const genOff = !config?.video_generation_enabled;
  const ready = review?.ready_for_one_generation;
  const armed = genOff && review?.estimate_valid && review?.approval_valid;
  return (
    <GlassCard className="p-6 border-red-500/30" data-testid="final-generation-review">
      <div className="flex items-center gap-2 mb-4">
        <PlayCircle className="w-4 h-4 text-red-400" />
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-red-300">Final Generation Review</div>
      </div>
      <div className={cx("mb-4 rounded-xl p-3 text-sm border flex items-center gap-2",
        genOff ? "bg-red-500/10 border-red-500/30 text-red-300" : "bg-emerald-500/10 border-emerald-500/25 text-emerald-300")}
        data-testid="generation-system-banner">
        <PowerOff className="w-4 h-4" />
        {armed ? "GENERATION SYSTEM READY — AWAITING EXPLICIT AUTHORIZATION"
          : <>GENERATION SYSTEM: <b>{genOff ? "DISABLED" : "ENABLED"}</b></>}
      </div>
      {review && (
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 mb-4">
          <KV k="Scene" v={review.scene?.slice(0, 22)} />
          <KV k="Version" v={`v${review.version}`} />
          <KV k="Provider" v={review.provider || "—"} />
          <KV k="Model" v={review.model} />
          <KV k="Endpoint" v={review.endpoint || "—"} />
          <KV k="Duration" v={`${review.duration_s}s`} />
          <KV k="Resolution" v={review.resolution} />
          <KV k="Aspect ratio" v={review.aspect_ratio || "16:9"} />
          <KV k="Takes" v={review.num_takes ?? 1} />
          <KV k="Audio" v={review.audio ? "ON" : "OFF"} />
          <KV k="Estimated" v={review.estimated_cost != null ? `$${review.estimated_cost}` : "—"} />
          <KV k="Hard cap" v={review.hard_spend_cap != null ? `$${review.hard_spend_cap}` : "—"} />
          <KV k="Retry" v="DISABLED" /><KV k="Fallback" v="DISABLED" />
          <KV k="Variants" v="DISABLED" /><KV k="Generations" v="1" />
          <KV k="Fingerprint" v={review.fingerprint?.slice(0, 16) || "—"} />
          <KV k="Estimate ID" v={review.estimate_id?.slice(0, 16) || "—"} />
          <KV k="Estimate expiry" v={review.estimate_expiration ? String(review.estimate_expiration).slice(0, 16) : "—"} />
          <KV k="Generation" v={review.generation || (genOff ? "DISABLED" : "ENABLED")} />
          <KV k="Estimate" v={review.estimate_valid ? "VALID" : "INVALID"} />
          <KV k="Approval" v={review.approval_valid ? "VALID" : "INVALID"} />
          <KV k="Prev generation" v={review.previous_generation ? "EXISTS" : "NONE"} />
        </div>
      )}
      {review && !ready && (
        <div className="mb-3 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/25 rounded-xl p-2" data-testid="exec-blocked-reason">
          BLOCKED: {review.blocked_reason}
        </div>
      )}
      <div className="text-xs text-red-300 mb-3 flex items-center gap-2"><ShieldAlert className="w-3.5 h-3.5" /> ONE PAID GENERATION WILL BE SUBMITTED.</div>
      <label className="flex items-center gap-2 text-sm text-slate-300 mb-3">
        <input type="checkbox" data-testid="understand-credits-checkbox" checked={understand}
          onChange={(e) => setUnderstand(e.target.checked)} className="accent-[#FFB300]" />
        I understand this will consume paid credits.
      </label>
      <div className="flex flex-wrap gap-3">
        {genOff ? (
          <button data-testid="authorize-generation-button" disabled
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm bg-white/5 border border-white/10 text-slate-600 cursor-not-allowed">
            <Lock className="w-4 h-4" /> Authorize (system disabled)
          </button>
        ) : (
          <button data-testid="authorize-generation-button" disabled={!understand || !ready || busy} onClick={onAuthorize}
            className={cx("inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition",
              (understand && ready) ? "bg-red-500 text-white hover:brightness-110" : "bg-white/5 border border-white/10 text-slate-600 cursor-not-allowed")}>
            <PlayCircle className="w-4 h-4" /> Authorize One Generation
          </button>
        )}
        <button data-testid="dry-run-button" disabled={busy} onClick={onDryRun}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm bg-[#5B4DFF]/20 border border-[#5B4DFF]/40 text-[#c3b9ff] hover:bg-[#5B4DFF]/30">
          <PlayCircle className="w-4 h-4" /> Run Mock Execution
        </button>
      </div>

      {result && (
        <div className="mt-4 rounded-xl bg-white/5 border border-white/10 p-3" data-testid="execution-result">
          {result.status === "COMPLETED" ? (
            <>
              <div className="text-sm text-emerald-300 mb-2 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> RAW GENERATION COMPLETE {result.execution?.mock ? "(MOCK · $0.00)" : ""}</div>
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-white/5 border border-white/10 text-slate-300" data-testid="watch-raw"><Eye className="w-3.5 h-3.5" /> Watch Raw</span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-white/5 border border-white/10 text-slate-300" data-testid="download-raw"><Download className="w-3.5 h-3.5" /> Download Raw Master</span>
              </div>
              <div className="text-xs text-slate-500 mt-2">QA: <b className="text-emerald-300">{result.execution?.qa?.overall}</b> · cost ${result.execution?.actual_cost}</div>
            </>
          ) : result.status === "QA_FAILED" ? (
            <div className="text-sm text-amber-300" data-testid="qa-failed">RAW GENERATION FAILED QA — no auto-regeneration.</div>
          ) : result.status === "FAILED" ? (
            <div className="text-sm text-red-300" data-testid="exec-failed">GENERATION FAILED · {result.execution?.provider_message} · Retry DISABLED · Fallback DISABLED</div>
          ) : (
            <div className="text-sm text-amber-300" data-testid="exec-blocked">GENERATION BLOCKED — {result.reason || result.message}</div>
          )}
        </div>
      )}
    </GlassCard>
  );
}

export default function CinematicPlanner() {
  const [mode, setMode] = useState("presets");
  const [scenes, setScenes] = useState(null);
  const [active, setActive] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [busy, setBusy] = useState(false);
  const [freeText, setFreeText] = useState("");
  const [hardCap, setHardCap] = useState("");
  const [estimate, setEstimate] = useState(null);
  const [history, setHistory] = useState([]);
  const [library, setLibrary] = useState([]);
  const [libSearch, setLibSearch] = useState("");
  const [libStatus, setLibStatus] = useState("ALL");
  const [libSort, setLibSort] = useState("updated");
  const [editDraft, setEditDraft] = useState({});
  const [cmpA, setCmpA] = useState("");
  const [cmpB, setCmpB] = useState("");
  const [cmpResult, setCmpResult] = useState(null);
  const [execConfig, setExecConfig] = useState(null);
  const [execReview, setExecReview] = useState(null);
  const [execResult, setExecResult] = useState(null);
  const [execHistory, setExecHistory] = useState([]);
  const [understand, setUnderstand] = useState(false);
  const topRef = useRef(null);

  const loadHistory = () =>
    api.get("/cinematic/approvals").then(({ data }) => setHistory(data.approvals)).catch(() => {});
  const loadLibrary = () =>
    api.get("/cinematic/library").then(({ data }) => setLibrary(data.plans)).catch(() => {});
  const loadExecHistory = () =>
    api.get("/cinematic/execution/history").then(({ data }) => setExecHistory(data.executions)).catch(() => {});

  useEffect(() => {
    api.get("/cinematic/scenes").then(({ data }) => {
      setScenes(data.scenes);
      if (data.scenes.length) setActive(data.scenes[0].id);
    }).catch(() => toast.error("Failed to load scenes"));
    api.get("/cinematic/execution/config").then(({ data }) => setExecConfig(data)).catch(() => {});
    loadHistory(); loadLibrary(); loadExecHistory();
  }, []);

  const prepareExec = (ref) => {
    setExecResult(null); setUnderstand(false);
    const cap = parseFloat(hardCap);
    api.post("/cinematic/execution/prepare", { ref, max_spend: isNaN(cap) ? null : cap })
      .then(({ data }) => setExecReview(data)).catch(() => setExecReview(null));
  };

  const authorizeExec = async () => {
    setBusy(true);
    try {
      const cap = parseFloat(hardCap);
      const { data } = await api.post("/cinematic/execution/authorize",
        { ref: active, confirm: true, max_spend: isNaN(cap) ? null : cap,
          idempotency_key: `auth-${active}-${plan.fingerprint}` });
      setExecResult(data);
      if (data.status === "BLOCKED") toast(`GENERATION BLOCKED — ${data.reason}`, { icon: "🛑" });
      loadExecHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || "Authorization failed"); }
    finally { setBusy(false); }
  };

  const dryRunExec = async () => {
    setBusy(true);
    try {
      const cap = parseFloat(hardCap);
      const { data } = await api.post("/cinematic/execution/dry-run",
        { ref: active, max_spend: isNaN(cap) ? null : cap,
          idempotency_key: `dry-${active}-${plan.fingerprint}` });
      setExecResult(data);
      toast.success(data.message || "Mock execution complete ($0.00)");
      loadExecHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || "Dry-run failed"); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (plan?.fingerprint && mode !== "library") prepareExec(active);
    // eslint-disable-next-line
  }, [plan?.fingerprint]);

  const loadPlan = (ref) => {
    setLoadingPlan(true);
    setEstimate(null); setEditDraft({});
    api.get(`/cinematic/plan/${ref}`).then(({ data }) => {
      setPlan(data);
      if (data.approval_state?.estimate) setEstimate(data.approval_state.estimate);
    }).catch(() => toast.error("Failed to load plan")).finally(() => setLoadingPlan(false));
  };

  useEffect(() => { if (active && mode === "presets") loadPlan(active); }, [active, mode]);

  const buildFreeText = async () => {
    if (freeText.trim().length < 8) return toast.error("Describe the scene in a sentence or two.");
    setBusy(true); setEstimate(null);
    try {
      const { data } = await api.post("/cinematic/plan/freetext", { text: freeText });
      setPlan(data); setActive(data.ref); setEditDraft({});
      topRef.current?.scrollIntoView({ behavior: "smooth" });
      loadLibrary();
      toast.success(`Plan built · ${data.classification.label} (${data.classification.confidence} confidence)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to build plan"); }
    finally { setBusy(false); }
  };

  const savePlan = async () => {
    try { await api.post("/cinematic/library/save", { ref: active }); toast.success("Saved to library"); loadLibrary(); }
    catch { toast.error("Save failed"); }
  };
  const duplicatePlan = async (ref) => {
    setBusy(true);
    try {
      const { data } = await api.post("/cinematic/library/duplicate", { ref: ref || active });
      toast.success("Duplicated (new DRAFT lineage)"); loadLibrary();
      setMode("freetext"); setActive(data.ref); loadPlan(data.ref);
    } catch { toast.error("Duplicate failed"); } finally { setBusy(false); }
  };
  const archivePlan = async (ref) => {
    try { await api.post("/cinematic/library/archive", { ref }); toast("Archived", { icon: "📦" }); loadLibrary(); if (ref === active) loadPlan(ref); }
    catch { toast.error("Archive failed"); }
  };
  const restorePlan = async (ref) => {
    try { await api.post("/cinematic/library/restore", { ref }); toast.success("Restored"); loadLibrary(); if (ref === active) loadPlan(ref); }
    catch { toast.error("Restore failed"); }
  };
  const applyEdits = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/cinematic/library/edit", { ref: active, overrides: editDraft });
      toast.success("New version created — estimate & approval invalidated");
      setPlan(data); setActive(data.ref); setEstimate(null); setEditDraft({});
      loadLibrary(); loadHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || "Edit failed"); }
    finally { setBusy(false); }
  };
  const runCompare = async () => {
    if (!cmpA || !cmpB) return toast.error("Pick two plans to compare");
    try {
      const { data } = await api.get(`/cinematic/library/compare?a=${cmpA}&b=${cmpB}`);
      setCmpResult(data);
    } catch { toast.error("Compare failed"); }
  };
  const openPlan = (ref) => { setMode("freetext"); setActive(ref); loadPlan(ref); topRef.current?.scrollIntoView({ behavior: "smooth" }); };

  const getEstimate = async () => {
    setBusy(true);
    try {
      const cap = parseFloat(hardCap);
      const { data } = await api.post("/cinematic/estimate", {
        ref: active, max_spend: isNaN(cap) ? null : cap });
      setEstimate(data.estimate);
      if (data.estimate.status === "VERIFIED_TIMESTAMPED")
        toast.success(`Verified estimate: $${data.estimate.estimated_cost}`);
      else toast("Cost UNKNOWN — approval stays blocked", { icon: "⚠️" });
      loadPlan(active); prepareExec(active);
    } catch (e) { toast.error(e?.response?.data?.detail || "Estimate failed"); }
    finally { setBusy(false); }
  };

  const approve = async () => {
    setBusy(true);
    try {
      const cap = parseFloat(hardCap);
      const { data } = await api.post("/cinematic/approve", {
        ref: active, max_spend: isNaN(cap) ? null : cap });
      if (data.already_approved) toast("THIS PLAN HAS ALREADY BEEN APPROVED.", { icon: "✅" });
      else toast.success(data.message);
      setPlan((p) => ({ ...p, approval_state: { ...p.approval_state, generation_approved: true } }));
      loadHistory(); loadPlan(active); prepareExec(active);
    } catch (e) { toast.error(e?.response?.data?.detail || "Approval blocked"); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await api.post("/cinematic/cancel", { ref: active });
      toast("Approval cleared", { icon: "🚫" });
      setPlan((p) => ({ ...p, approval_state: { ...p.approval_state, generation_approved: false } }));
      loadHistory(); loadPlan(active);
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  if (!scenes) return <Spinner label="Loading Cinematic Planner…" />;
  const approved = plan?.approval_state?.generation_approved;

  return (
    <CinematicStage>
    <div ref={topRef}>
      <StageHeader stage="04–08" room="The Planning Room · Story · Scenes · Shots · Characters" icon={Film} title="Plan Preview & Approval Gate"
        subtitle="Describe any scene or pick a preset. The AI director plans camera, motion, VFX, physics, audio, continuity, risk & cost — then you get a verified estimate BEFORE any paid generation. Dry-run only; executor disabled."
        actions={<Pill tone="red"><Ban className="w-3 h-3" /> Zero Generation</Pill>} />

      {/* Mode toggle */}
      <div className="flex gap-2 mb-5">
        <button data-testid="mode-presets" onClick={() => setMode("presets")}
          className={cx("inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition",
            mode === "presets" ? "bg-[#5B4DFF]/20 text-white border-[#5B4DFF]/50" : "bg-white/5 text-slate-400 border-white/10 hover:text-white")}>
          <LayoutGrid className="w-4 h-4" /> Presets
        </button>
        <button data-testid="mode-freetext" onClick={() => setMode("freetext")}
          className={cx("inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition",
            mode === "freetext" ? "bg-[#5B4DFF]/20 text-white border-[#5B4DFF]/50" : "bg-white/5 text-slate-400 border-white/10 hover:text-white")}>
          <PenLine className="w-4 h-4" /> Describe your video
        </button>
        <button data-testid="mode-library" onClick={() => { setMode("library"); loadLibrary(); }}
          className={cx("inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition",
            mode === "library" ? "bg-[#5B4DFF]/20 text-white border-[#5B4DFF]/50" : "bg-white/5 text-slate-400 border-white/10 hover:text-white")}>
          <FolderOpen className="w-4 h-4" /> Library
        </button>
      </div>

      {mode === "library" && (
        <LibraryView library={library} search={libSearch} setSearch={setLibSearch}
          statusFilter={libStatus} setStatusFilter={setLibStatus} sort={libSort} setSort={setLibSort}
          onOpen={openPlan} onDuplicate={duplicatePlan} onArchive={archivePlan} onRestore={restorePlan}
          cmpA={cmpA} cmpB={cmpB} setCmpA={setCmpA} setCmpB={setCmpB} onCompare={runCompare} cmpResult={cmpResult} />
      )}

      {mode === "presets" && (
        <div className="flex flex-wrap gap-2 mb-6">
          {scenes.map((s) => (
            <button key={s.id} data-testid={`scene-tab-${s.id}`} onClick={() => setActive(s.id)}
              className={cx("px-4 py-2 rounded-xl text-sm font-medium border transition",
                active === s.id ? "bg-[#5B4DFF]/20 text-white border-[#5B4DFF]/50" : "bg-white/5 text-slate-400 border-white/10 hover:text-white")}>
              {s.title}
            </button>
          ))}
        </div>
      )}
      {mode === "freetext" && (
        <GlassCard className="p-6 mb-6" data-testid="freetext-panel">
          <SectionTitle icon={Wand2}>Describe your video</SectionTitle>
          <textarea data-testid="freetext-input" value={freeText} onChange={(e) => setFreeText(e.target.value)}
            rows={4} placeholder='e.g. "A young woman enters a rainy café, recognizes a man by the window, walks to his table, asks &quot;Is this seat free?&quot; and sits down."'
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-[#5B4DFF]/50 resize-y" />
          <div className="flex items-center justify-between mt-3">
            {plan?.classification && plan.source === "free_text" ? (
              <div className="flex items-center gap-2 text-xs" data-testid="classification-badge">
                <span className="text-slate-500 font-mono uppercase">Classified</span>
                <Pill tone="slate">{plan.classification.label}</Pill>
                <Pill tone={plan.classification.confidence === "LOW" ? "gold" : "green"}>
                  {plan.classification.confidence} confidence
                </Pill>
                {plan.classification.editable && <span className="text-amber-300">— review/edit suggested</span>}
              </div>
            ) : <span />}
            <button data-testid="build-plan-button" disabled={busy} onClick={buildFreeText}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm bg-[#5B4DFF] text-white hover:brightness-110 transition glow-violet">
              <Wand2 className="w-4 h-4" /> Build ScenePlan
            </button>
          </div>
        </GlassCard>
      )}

      {mode !== "library" && (loadingPlan || !plan ? (
        mode === "freetext" && !plan ? null : <Spinner label="Assembling ScenePlan…" />
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6" data-testid="plan-preview">
          <div className="xl:col-span-2 space-y-8">
            {/* Story */}
            <GlassCard className="p-6">
              <SectionTitle icon={Film}>Story</SectionTitle>
              <div className="grid sm:grid-cols-2 gap-x-8">
                <KV k="Title" v={plan.story.title} />
                <KV k="Classification" v={plan.story.classification} />
                <KV k="Duration" v={`${plan.story.estimated_duration_s}s`} />
                <KV k="Hook" v={plan.story.hook ? "Recommended" : "None (story-first)"} />
              </div>
              <div className="mt-4 space-y-1.5">
                {plan.story.beats.map((b) => (
                  <div key={b.id} className="flex items-center gap-3 text-sm" data-testid={`story-beat-${b.id}`}>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-[#FFB300] w-24 shrink-0">{b.type}</span>
                    <span className="text-slate-300">{b.description}</span>
                    {b.dialogue?.line && <span className="text-xs text-emerald-300 ml-auto shrink-0">“{b.dialogue.line}”</span>}
                  </div>
                ))}
              </div>
            </GlassCard>

            {/* Shots */}
            <div>
              <SectionTitle icon={Clapperboard}>Shot-by-shot timeline</SectionTitle>
              <div className="space-y-4">
                {plan.shots.map((s, i) => (
                  <GlassCard key={s.shot_id} className="p-5" data-testid={`shot-card-${s.shot_id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="font-display font-semibold text-white">
                        SHOT {String(i + 1).padStart(2, "0")} — <span className="text-[#FFB300] uppercase">{s.beat_type}</span>
                      </div>
                      <Pill tone="slate">{s.duration_s}s</Pill>
                    </div>
                    <p className="text-sm text-slate-300 mb-4">{s.story_beat}</p>
                    <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1 mb-4">
                      <KV k="Framing" v={s.camera.framing.replace(/_/g, " ")} />
                      <KV k="Distance" v={s.camera.distance} />
                      <KV k="Movement" v={s.camera.movement.replace(/_/g, " ")} />
                      <KV k="Physics" v={s.physics ? s.physics.action : "—"} />
                    </div>
                    <div className="mb-3">
                      <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-1.5">Motion</div>
                      <SpeedFlow seq={s.motion_sequence} />
                    </div>
                    {s.camera_safety && (
                      <div className="mb-3 rounded-xl bg-emerald-500/5 border border-emerald-500/20 p-3" data-testid={`camera-safety-${s.shot_id}`}>
                        <div className="text-xs font-mono uppercase tracking-wider text-emerald-300 mb-2 flex items-center gap-1.5">
                          <ScanEye className="w-3.5 h-3.5" /> Dialogue camera safety
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                          {Object.entries(s.camera_safety).map(([rule, ok]) => (
                            <div key={rule} className="flex items-center gap-1.5 text-xs">
                              {ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <XCircle className="w-3.5 h-3.5 text-red-400" />}
                              <span className="text-slate-300">{rule}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div>
                        <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-1.5">VFX selected</div>
                        <div className="flex flex-wrap gap-1.5">
                          {s.vfx_selected.length ? s.vfx_selected.map((v, vi) => (
                            <span key={`${v.effect}-${vi}`} title={v.reason} className="px-2 py-1 rounded-md bg-[#5B4DFF]/10 border border-[#5B4DFF]/25 text-xs text-[#b6aaff]">
                              {v.effect.replace(/_/g, " ")} · <span className="text-slate-500">{v.intensity}</span>
                            </span>
                          )) : <span className="text-xs text-slate-600">none</span>}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-1.5">VFX rejected</div>
                        <div className="flex flex-wrap gap-1.5">
                          {s.vfx_rejected.length ? s.vfx_rejected.map((v, vi) => (
                            <span key={`${v.effect}-${vi}`} title={v.reason} className="px-2 py-1 rounded-md bg-red-500/10 border border-red-500/25 text-xs text-red-300 line-through">
                              {v.effect.replace(/_/g, " ")}
                            </span>
                          )) : <span className="text-xs text-slate-600">none</span>}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
                      <AudioLines className="w-3.5 h-3.5" /> Audio: {s.audio_hierarchy.join(" → ")}{s.audio_ducking ? " (ducking)" : ""}
                    </div>
                  </GlassCard>
                ))}
              </div>
            </div>

            {/* Continuity bible */}
            <GlassCard className="p-6">
              <SectionTitle icon={BookMarked}>Continuity Bible</SectionTitle>
              <div className="grid sm:grid-cols-2 gap-x-8">
                <KV k="Characters" v={plan.continuity_bible.characters.join(", ")} />
                <KV k="Location" v={plan.continuity_bible.location} />
                <KV k="Wardrobe" v={plan.continuity_bible.wardrobe} />
                <KV k="Lighting" v={plan.continuity_bible.lighting} />
                <KV k="Props" v={plan.continuity_bible.props.join(", ")} />
                <KV k="Geography" v={plan.continuity_bible.camera_geography} />
              </div>
              {plan.continuity_bible.scene_specific && (
                <div className="mt-4 rounded-xl bg-white/5 border border-white/10 p-4" data-testid="continuity-scene-specific">
                  <div className="text-xs font-mono uppercase tracking-wider text-[#FFB300] mb-2">Scene-specific (locked)</div>
                  {Object.entries(plan.continuity_bible.scene_specific).map(([k, v]) => <KV key={k} k={k} v={v} />)}
                </div>
              )}
            </GlassCard>

            {plan.source === "free_text" && (
              <EditPanel plan={plan} draft={editDraft} setDraft={setEditDraft} onApply={applyEdits} busy={busy} />
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* plan actions + status */}
            <GlassCard className="p-4" data-testid="plan-actions">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Pill tone={STATUS_TONE[plan.status] || "slate"} data-testid="plan-status">{plan.status}</Pill>
                  <Pill tone="slate">v{plan.version}</Pill>
                </div>
                {plan.classification_source === "USER" && <Pill tone="gold">user-set category</Pill>}
              </div>
              {plan.source === "free_text" && (
                <div className="flex flex-wrap gap-2">
                  <button data-testid="save-plan-button" onClick={savePlan}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-white/5 border border-white/10 text-slate-200 hover:bg-white/10">
                    <Save className="w-3.5 h-3.5" /> Save</button>
                  <button data-testid="duplicate-plan-button" onClick={() => duplicatePlan(active)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-white/5 border border-white/10 text-slate-200 hover:bg-white/10">
                    <Copy className="w-3.5 h-3.5" /> Duplicate</button>
                  {plan.archived ? (
                    <button data-testid="restore-plan-button" onClick={() => restorePlan(active)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-white/5 border border-white/10 text-slate-200 hover:bg-white/10">
                      <RotateCcw className="w-3.5 h-3.5" /> Restore</button>
                  ) : (
                    <button data-testid="archive-plan-button" onClick={() => archivePlan(active)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-white/5 border border-white/10 text-slate-200 hover:bg-white/10">
                      <Archive className="w-3.5 h-3.5" /> Archive</button>
                  )}
                </div>
              )}
            </GlassCard>

            <ApprovalCard plan={plan} estimate={estimate} hardCap={hardCap} setHardCap={setHardCap}
              onEstimate={getEstimate} onApprove={approve} onCancel={cancel} busy={busy} approved={approved} />

            {plan.safety_checklist && <SafetyChecklist checklist={plan.safety_checklist} />}

            <ExecutionPanel config={execConfig} review={execReview} understand={understand}
              setUnderstand={setUnderstand} onAuthorize={authorizeExec} onDryRun={dryRunExec}
              result={execResult} busy={busy} />

            <ExplanationsCard ex={plan.explanations} />

            {/* Model */}
            <GlassCard className="p-6" data-testid="model-card">
              <SectionTitle icon={Cpu}>Model</SectionTitle>
              {plan.model.chosen ? (
                <>
                  <div className="text-white font-display text-lg">{plan.model.chosen.name}</div>
                  <div className="text-xs text-slate-500 font-mono mb-3">{plan.model.chosen.provider} · {plan.model.chosen.endpoint}</div>
                  <ul className="space-y-1 mb-4">
                    {plan.model.chosen.reasons.map((r, i) => (
                      <li key={i} className="text-sm text-slate-300 flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />{r}</li>
                    ))}
                  </ul>
                </>
              ) : <div className="text-red-300 text-sm">No compatible model resolved.</div>}
              {plan.model.rejected.length > 0 && (
                <div>
                  <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">Rejected alternatives</div>
                  <div className="space-y-1.5">
                    {plan.model.rejected.map((r) => (
                      <div key={r.key} className="text-xs text-slate-400 flex gap-2" data-testid={`rejected-model-${r.key}`}>
                        <Ban className="w-3.5 h-3.5 text-red-400 shrink-0 mt-0.5" />
                        <span><b className="text-slate-300">{r.name}</b> — {r.reasons[0]}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </GlassCard>

            {/* Delivery */}
            <GlassCard className="p-6" data-testid="delivery-card">
              <SectionTitle icon={Film}>Delivery</SectionTitle>
              <div className="flex items-center gap-2 mb-2">
                <Pill tone={plan.delivery.native_4k ? "green" : "gold"}>{plan.delivery.label.replace(/_/g, " ")}</Pill>
              </div>
              <p className="text-sm text-slate-400">{plan.delivery.note}</p>
            </GlassCard>

            {/* Risk */}
            <GlassCard className="p-6" data-testid="risk-panel">
              <SectionTitle icon={Shield}>Risk Panel</SectionTitle>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xs font-mono uppercase tracking-wider text-slate-500">Overall</span>
                <Pill tone={RISK_TONE[plan.risk_panel.rollup]}>{plan.risk_panel.rollup}</Pill>
              </div>
              <div className="space-y-2.5">
                {plan.risk_panel.items.map((r) => (
                  <div key={r.category} data-testid={`risk-${r.category}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-200">{r.category}</span>
                      <Pill tone={RISK_TONE[r.level]} className="!px-2 !py-0.5">{r.level}</Pill>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{r.reason}</p>
                  </div>
                ))}
              </div>
            </GlassCard>

            {plan.continuity_warnings.length > 0 && (
              <GlassCard className="p-6 border-amber-500/25" data-testid="continuity-warnings">
                <SectionTitle icon={AlertTriangle}>Continuity Warnings</SectionTitle>
                <ul className="space-y-2">
                  {plan.continuity_warnings.map((n, i) => (
                    <li key={i} className="text-sm text-amber-300 flex gap-2"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{n}</li>
                  ))}
                </ul>
              </GlassCard>
            )}
          </div>
        </div>
      ))}

      {/* Approval history */}
      {mode !== "library" && (
      <GlassCard className="p-6 mt-8" data-testid="approval-history">
        <SectionTitle icon={History}>Approval History</SectionTitle>
        {history.length === 0 ? (
          <p className="text-sm text-slate-500">No approvals yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-mono uppercase tracking-wider text-slate-500 border-b border-white/10">
                  <th className="py-2 pr-4">Scene</th><th className="py-2 pr-4">Model</th>
                  <th className="py-2 pr-4">Cost</th><th className="py-2 pr-4">Res</th>
                  <th className="py-2 pr-4">Status</th><th className="py-2 pr-4">Gen</th>
                  <th className="py-2">When</th>
                </tr>
              </thead>
              <tbody>
                {history.map((a, i) => (
                  <tr key={i} className="border-b border-white/5" data-testid={`history-row-${i}`}>
                    <td className="py-2 pr-4 text-slate-200">{(a.title || a.ref || "").slice(0, 32)}</td>
                    <td className="py-2 pr-4 text-slate-400">{a.model || "—"}</td>
                    <td className="py-2 pr-4 text-slate-300">{a.estimated_cost != null ? `$${a.estimated_cost}` : "UNKNOWN"}</td>
                    <td className="py-2 pr-4 text-slate-400">{a.resolution || "—"}</td>
                    <td className="py-2 pr-4"><Pill tone={STATUS_TONE[a.status] || "slate"}>{a.status || "—"}</Pill></td>
                    <td className="py-2 pr-4 text-red-300 text-xs">DISABLED</td>
                    <td className="py-2 text-slate-500 text-xs">{a.approved_at ? new Date(a.approved_at).toLocaleString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>
      )}

      {/* Execution history */}
      <GlassCard className="p-6 mt-8" data-testid="execution-history">
        <SectionTitle icon={PlayCircle}>Execution History</SectionTitle>
        <div className="text-xs text-slate-500 mb-3">Real generations: <b className="text-red-300">0</b> · Generation system: <b>{execConfig?.video_generation_enabled ? "ENABLED" : "DISABLED"}</b></div>
        {execHistory.length === 0 ? <p className="text-sm text-slate-500">No executions yet.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-mono uppercase tracking-wider text-slate-500 border-b border-white/10">
                  <th className="py-2 pr-4">Exec ID</th><th className="py-2 pr-4">Model</th><th className="py-2 pr-4">Cost</th>
                  <th className="py-2 pr-4">Status</th><th className="py-2 pr-4">QA</th><th className="py-2 pr-4">Mock</th><th className="py-2">Started</th>
                </tr>
              </thead>
              <tbody>
                {execHistory.map((e, i) => (
                  <tr key={e.token || i} className="border-b border-white/5" data-testid={`exec-row-${i}`}>
                    <td className="py-2 pr-4 text-slate-300 font-mono text-xs">{(e.token || "").slice(0, 20)}</td>
                    <td className="py-2 pr-4 text-slate-400">{e.model || "—"}</td>
                    <td className="py-2 pr-4 text-slate-300">{e.actual_cost != null ? `$${e.actual_cost}` : "—"}</td>
                    <td className="py-2 pr-4"><Pill tone={e.status === "COMPLETED" ? "green" : e.status === "FAILED" || e.status === "QA_FAILED" ? "red" : "slate"}>{e.status}</Pill></td>
                    <td className="py-2 pr-4 text-slate-400">{e.qa?.overall || "—"}</td>
                    <td className="py-2 pr-4 text-slate-500 text-xs">{e.mock ? "MOCK" : "real"}</td>
                    <td className="py-2 text-slate-500 text-xs">{e.started_at ? new Date(e.started_at).toLocaleString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>
    </div>
    </CinematicStage>
  );
}
